import { WEBHOOK_HEADERS } from '@crm/contracts';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  History,
  Pause,
  Pencil,
  Play,
  Plus,
  RotateCw,
  Send,
  Trash2,
  Webhook,
} from 'lucide-react';
import { useState } from 'react';
import { Gate } from '../components/shell/gate.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card } from '../components/ui/card.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.tsx';
import { EmptyState } from '../components/ui/empty-state.tsx';
import { Input } from '../components/ui/input.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';
import { describeError } from '../lib/api.ts';
import {
  api,
  type WebhookDeliveryView,
  type WebhookView,
  type WebhookWithSecretView,
} from '../lib/client.ts';
import { useSession } from '../lib/session.tsx';
import { cn } from '../lib/utils.ts';
import { Textarea } from '../components/ui/input.tsx';
import { nativeSelectClass } from '../components/ui/input.tsx';

const QUERY = ['webhooks'] as const;

/** The events a person can pick, by record kind (the catalogue's names; `*` per kind or all). */
const EVENT_GROUPS: Array<{ entity: string; label: string; events: string[] }> = [
  {
    entity: 'candidate',
    label: 'Candidates',
    // `stage_changed` is a move on a shortlist; the pipeline_* ones are the candidate's own
    // Candidate Flow places.
    events: [
      'created',
      'updated',
      'deleted',
      'restored',
      'stage_changed',
      'shortlist_archived',
      'shortlist_restored',
      'pipeline_stage_changed',
      'pipeline_added',
      'pipeline_archived',
      'pipeline_restored',
    ],
  },
  { entity: 'company', label: 'Companies', events: ['created', 'updated', 'deleted', 'restored'] },
  { entity: 'contact', label: 'Contacts', events: ['created', 'updated', 'deleted', 'restored'] },
  {
    entity: 'project',
    label: 'Projects',
    events: ['created', 'updated', 'deleted', 'restored', 'stage_changed'],
  },
  {
    entity: 'deal',
    label: 'Deals',
    events: ['created', 'updated', 'deleted', 'restored', 'status_changed'],
  },
  {
    entity: 'job',
    label: 'Jobs',
    events: ['created', 'updated', 'deleted', 'restored', 'status_changed'],
  },
  // TS-1833: published for the RecruitCRM sync; a note has no restore route yet, so
  // `restored` is in the catalogue but nothing emits it today.
  { entity: 'note', label: 'Notes', events: ['created', 'updated', 'deleted', 'restored'] },
];
const EVENT_LABEL: Record<string, string> = {
  created: 'Created',
  updated: 'Updated',
  deleted: 'Deleted',
  restored: 'Restored',
  stage_changed: 'Stage changed',
  status_changed: 'Status changed',
  pipeline_stage_changed: 'Pipeline stage changed',
  pipeline_added: 'Added to a pipeline',
  pipeline_archived: 'Archived in a pipeline',
  pipeline_restored: 'Restored in a pipeline',
  shortlist_archived: 'Taken off a shortlist',
  shortlist_restored: 'Put back on a shortlist',
};

const STATUS_VARIANT = {
  pending: 'secondary',
  retrying: 'warning',
  succeeded: 'success',
  dead: 'danger',
} as const;

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

/**
 * Settings → Webhooks (TS-958): where an admin points other systems at the CRM's events. Each
 * subscription is a URL, the events it wants and an optional filter; the worker POSTs each
 * matching event, signed with the subscription's secret, retrying for a day. This page shows
 * whether that is working (7-day counts, the delivery log) and lets an admin test a URL,
 * pause it, rotate its secret and replay a delivery.
 */
export function WebhooksPage() {
  const session = useSession();
  const qc = useQueryClient();
  const canManage = session.can('integration.manage');
  const hooks = useQuery({ queryKey: QUERY, queryFn: api.webhooks.list });
  const [editing, setEditing] = useState<WebhookView | 'new' | null>(null);
  const [secret, setSecret] = useState<WebhookWithSecretView | null>(null);
  const [deleting, setDeleting] = useState<WebhookView | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: QUERY });
  const onError = (err: unknown) => setError(describeError(err));

  const pause = useMutation({
    mutationFn: (h: WebhookView) =>
      api.webhooks.patch(h.id, { status: h.status === 'active' ? 'paused' : 'active' }),
    onSuccess: async (h) => {
      setError(null);
      setNotice(
        h.status === 'paused'
          ? 'Paused: nothing is sent, and events that happen while paused are not kept. Deliveries already queued go out when you resume.'
          : 'Resumed.',
      );
      await refresh();
    },
    onError,
  });
  const rotate = useMutation({
    mutationFn: (h: WebhookView) => api.webhooks.rotate(h.id, 60),
    onSuccess: async (h) => {
      setError(null);
      setSecret(h);
      await refresh();
    },
    onError,
  });
  const test = useMutation({
    mutationFn: (h: WebhookView) => api.webhooks.test(h.id),
    onSuccess: async (_d, h) => {
      setError(null);
      setNotice('Test event queued: it arrives within seconds; see its result in Deliveries.');
      setOpen(h.id);
      await qc.invalidateQueries({ queryKey: ['webhook-deliveries', h.id] });
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: (h: WebhookView) => api.webhooks.remove(h.id),
    onSuccess: async () => {
      setDeleting(null);
      setError(null);
      setNotice('Webhook removed. Its delivery log is kept.');
      await refresh();
    },
    onError,
  });

  const list = hooks.data ?? [];
  return (
    <Gate permission="integration.read" feature="webhooks.enabled">
      <PageHeader
        title="Webhooks"
        description="Send record changes to another system the moment they happen: each event is POSTed, signed, to the URLs subscribed to it, and retried for a day if the receiver is down."
        actions={
          canManage && (
            <Button onClick={() => setEditing('new')} data-testid="new-webhook">
              <Plus /> New webhook
            </Button>
          )
        }
      />
      {notice && (
        <p
          className="bg-state-success-bg text-state-success-fg border-state-success-border mb-5 rounded-xl border px-4 py-3 text-sm"
          role="status"
        >
          {notice}
        </p>
      )}
      {error && (
        <p
          className="bg-state-danger-bg text-state-danger-fg border-state-danger-border mb-5 rounded-xl border px-4 py-3 text-sm"
          role="alert"
        >
          {error}
        </p>
      )}
      {hooks.isError && (
        <p className="text-danger text-sm" role="alert">
          {describeError(hooks.error)}
        </p>
      )}
      {hooks.data && list.length === 0 && (
        <EmptyState
          icon={Webhook}
          title="No webhooks yet."
          description="Add one for each system that should hear about changes, with only the events it needs."
        />
      )}

      <div className="flex flex-col gap-4">
        {list.map((h) => (
          <Card key={h.id} className="overflow-hidden" data-testid="webhook">
            <div className="flex flex-wrap items-start gap-x-6 gap-y-4 p-5">
              <div className="flex min-w-0 flex-1 basis-80 items-start gap-3">
                <span
                  className={cn(
                    'grid size-10 shrink-0 place-items-center rounded-xl',
                    h.status === 'active'
                      ? 'bg-brand-soft text-brand-soft-foreground'
                      : 'bg-muted text-muted-foreground',
                  )}
                >
                  <Webhook className="size-[18px]" aria-hidden />
                </span>
                <div className="min-w-0">
                  <p className="font-mono text-[13px] font-semibold break-all">{h.url}</p>
                  {h.description && (
                    <p className="text-muted-foreground mt-0.5 text-sm">{h.description}</p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Badge variant={h.status === 'active' ? 'success' : 'secondary'}>
                      {h.status}
                    </Badge>
                    {h.events.map((e) => (
                      <Badge key={e} variant="outline" className="font-mono">
                        {e}
                      </Badge>
                    ))}
                    {h.filter && <Badge variant="brand">filtered</Badge>}
                  </div>
                </div>
              </div>
              <dl className="grid grid-cols-3 gap-2 text-center text-xs">
                <Stat label="Delivered" value={h.stats.succeeded} tone="success" />
                <Stat label="Retrying" value={h.stats.failing} tone="warning" />
                <Stat label="Failed" value={h.stats.dead} tone="danger" />
              </dl>
            </div>
            <div className="border-border bg-muted/40 flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3">
              <p className="text-muted-foreground text-xs">
                Last delivery {when(h.stats.lastDeliveryAt)}
                {h.stats.lastStatus && ` · ${h.stats.lastStatus}`} · secret ••••{h.secretHint}
                {h.previousSecretExpiresAt &&
                  ` · old secret valid until ${when(h.previousSecretExpiresAt)}`}
              </p>
              <span className="flex flex-wrap items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setOpen(open === h.id ? null : h.id)}
                  aria-expanded={open === h.id}
                  data-testid={`deliveries-${h.id}`}
                >
                  {open === h.id ? <ChevronDown /> : <ChevronRight />} <History /> Deliveries
                </Button>
                {canManage && (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => test.mutate(h)}
                      disabled={test.isPending}
                    >
                      <Send /> Send test
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => pause.mutate(h)}
                      disabled={pause.isPending}
                    >
                      {h.status === 'active' ? <Pause /> : <Play />}
                      {h.status === 'active' ? 'Pause' : 'Resume'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(h)}>
                      <Pencil /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => rotate.mutate(h)}
                      disabled={rotate.isPending}
                    >
                      <RotateCw /> Rotate secret
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-danger hover:text-danger"
                      onClick={() => setDeleting(h)}
                      aria-label={`Delete ${h.url}`}
                    >
                      <Trash2 />
                    </Button>
                  </>
                )}
              </span>
            </div>
            {open === h.id && <Deliveries hook={h} canReplay={canManage} />}
          </Card>
        ))}
      </div>

      <ReceiverNotes />

      {editing && (
        <WebhookEditor
          hook={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async (h) => {
            setEditing(null);
            setError(null);
            if ('secret' in h) setSecret(h);
            else setNotice('Webhook saved.');
            await refresh();
          }}
        />
      )}
      {secret && <SecretDialog hook={secret} onClose={() => setSecret(null)} />}
      <Dialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this webhook?</DialogTitle>
            <DialogDescription>
              Nothing more is sent to {deleting?.url}. Its delivery log is kept.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleting && remove.mutate(deleting)}
              disabled={remove.isPending}
              data-testid="confirm-delete-webhook"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Gate>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'success' | 'warning' | 'danger';
}) {
  const color = {
    success: 'text-state-success-fg',
    warning: 'text-state-warning-fg',
    danger: 'text-state-danger-fg',
  }[tone];
  return (
    <div className="bg-muted/60 border-border min-w-20 rounded-xl border px-3 py-2">
      <dd className={cn('text-lg font-bold tabular-nums', value > 0 ? color : '')}>{value}</dd>
      <dt className="text-muted-foreground">{label}</dt>
    </div>
  );
}

/** A subscription's delivery log, newest first, with replay and each body on demand. */
function Deliveries({ hook, canReplay }: { hook: WebhookView; canReplay: boolean }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<WebhookDeliveryView['status'] | ''>('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const page = useInfiniteQuery({
    queryKey: ['webhook-deliveries', hook.id, status],
    queryFn: ({ pageParam }) =>
      api.webhooks.deliveries(hook.id, {
        ...(status ? { status } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 5_000,
  });
  const replay = useMutation({
    mutationFn: (d: WebhookDeliveryView) => api.webhooks.replay(d.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhook-deliveries', hook.id] }),
  });
  const rows = page.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="border-border border-t px-5 py-4" data-testid="delivery-log">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-muted-foreground text-xs font-bold tracking-[0.08em] uppercase">
          Deliveries
        </p>
        <select
          aria-label="Filter deliveries"
          value={status}
          onChange={(e) => setStatus(e.target.value as WebhookDeliveryView['status'] | '')}
          className={cn(nativeSelectClass, 'h-8 text-xs')}
        >
          <option value="">All</option>
          <option value="succeeded">Delivered</option>
          <option value="retrying">Retrying</option>
          <option value="pending">Pending</option>
          <option value="dead">Failed</option>
        </select>
      </div>
      {page.isPending && <p className="text-muted-foreground text-sm">Loading…</p>}
      {page.isError && (
        <p className="text-danger text-sm" role="alert">
          {describeError(page.error)}
        </p>
      )}
      {page.data && rows.length === 0 && (
        <p className="text-muted-foreground py-4 text-center text-sm">
          Nothing yet. Use “Send test” to check the URL.
        </p>
      )}
      {rows.length > 0 && (
        <div className="bg-card border-border overflow-x-auto rounded-xl border">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="text-muted-foreground bg-muted text-xs whitespace-nowrap">
              <tr>
                <th className="px-3 py-2 font-semibold">When</th>
                <th className="px-3 py-2 font-semibold">Event</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Attempts</th>
                <th className="px-3 py-2 font-semibold">Response</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <DeliveryRow
                  key={d.id}
                  d={d}
                  expanded={expanded === d.id}
                  onToggle={() => setExpanded(expanded === d.id ? null : d.id)}
                  onReplay={canReplay ? () => replay.mutate(d) : undefined}
                  replaying={replay.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {page.hasNextPage && (
        <Button
          size="sm"
          variant="outline"
          className="mt-3"
          onClick={() => void page.fetchNextPage()}
          disabled={page.isFetchingNextPage}
        >
          Load more
        </Button>
      )}
    </div>
  );
}

function DeliveryRow({
  d,
  expanded,
  onToggle,
  onReplay,
  replaying,
}: {
  d: WebhookDeliveryView;
  expanded: boolean;
  onToggle: () => void;
  onReplay: (() => void) | undefined;
  replaying: boolean;
}) {
  return (
    <>
      <tr className="border-border border-t" data-testid="delivery">
        <td className="text-muted-foreground px-3 py-2 whitespace-nowrap">{when(d.createdAt)}</td>
        <td className="px-3 py-2 font-mono text-xs">
          {d.eventName}
          {d.replayOf && <span className="text-muted-foreground ml-1 font-sans">(replay)</span>}
        </td>
        <td className="px-3 py-2">
          <Badge variant={STATUS_VARIANT[d.status]}>{d.status}</Badge>
        </td>
        <td className="px-3 py-2 tabular-nums">{d.attempts}</td>
        <td className="text-muted-foreground max-w-64 px-3 py-2 text-xs">
          <span className="block truncate" title={d.lastError ?? undefined}>
            {d.lastStatusCode ?? ''} {d.lastError ?? (d.status === 'succeeded' ? 'OK' : '')}
            {d.nextAttemptAt && d.status === 'retrying' && ` · next ${when(d.nextAttemptAt)}`}
          </span>
        </td>
        <td className="px-3 py-2 text-right whitespace-nowrap">
          <Button size="sm" variant="ghost" onClick={onToggle} aria-expanded={expanded}>
            {expanded ? 'Hide' : 'Body'}
          </Button>
          {onReplay && (
            <Button
              size="sm"
              variant="outline"
              onClick={onReplay}
              disabled={replaying}
              aria-label={`Replay ${d.eventName} from ${when(d.createdAt)}`}
            >
              <RotateCw /> Replay
            </Button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="border-border border-t">
          <td colSpan={6} className="bg-muted/40 px-3 py-3">
            <pre className="max-h-72 overflow-auto font-mono text-xs leading-relaxed whitespace-pre-wrap">
              {JSON.stringify(d.body, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

/** Create or edit: URL, description, events (per record kind or all), an optional filter. */
function WebhookEditor({
  hook,
  onClose,
  onSaved,
}: {
  hook: WebhookView | null;
  onClose: () => void;
  onSaved: (h: WebhookView | WebhookWithSecretView) => Promise<void>;
}) {
  const [url, setUrl] = useState(hook?.url ?? '');
  const [description, setDescription] = useState(hook?.description ?? '');
  const [events, setEvents] = useState<ReadonlySet<string>>(new Set(hook?.events ?? []));
  const [filterText, setFilterText] = useState(
    hook?.filter ? JSON.stringify(hook.filter, null, 2) : '',
  );
  const [error, setError] = useState<string | null>(null);
  const toggle = (e: string) =>
    setEvents((cur) => {
      const next = new Set(cur);
      if (next.has(e)) next.delete(e);
      else next.add(e);
      return next;
    });
  const all = events.has('*');
  const save = useMutation({
    mutationFn: async () => {
      let filter: Record<string, Array<string | number | boolean>> | null = null;
      if (filterText.trim()) {
        try {
          filter = JSON.parse(filterText) as typeof filter;
        } catch (err) {
          throw new Error(`The filter is not valid JSON (${String(err)})`, { cause: err });
        }
      }
      const body = {
        url: url.trim(),
        description: description.trim(),
        events: [...events],
        filter,
      };
      return hook ? api.webhooks.patch(hook.id, body) : api.webhooks.create(body);
    },
    onSuccess: onSaved,
    onError: (err) => setError(describeError(err)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <form
          className="flex flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{hook ? 'Edit webhook' : 'New webhook'}</DialogTitle>
            <DialogDescription>
              The receiving system's URL (https), and which events it should get.
            </DialogDescription>
          </DialogHeader>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            URL
            <Input
              required
              type="url"
              aria-label="Webhook URL"
              placeholder="https://example.com/crm-events"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Description
            <Input
              aria-label="Webhook description"
              placeholder="What receives it, e.g. Relay task board"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <fieldset className="border-border flex flex-col gap-3 rounded-xl border p-4">
            <legend className="px-1 text-sm font-semibold">Events</legend>
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                className="accent-brand size-4"
                checked={all}
                onChange={() => toggle('*')}
                aria-label="Every event"
              />
              Every event
            </label>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {EVENT_GROUPS.map((g) => {
                const wildcard = `crm.${g.entity}.*`;
                const groupAll = all || events.has(wildcard);
                return (
                  <div key={g.entity} className="bg-muted/40 border-border rounded-lg border p-3">
                    <label className="flex items-center gap-2 text-sm font-semibold">
                      <input
                        type="checkbox"
                        className="accent-brand size-4"
                        checked={groupAll}
                        disabled={all}
                        onChange={() => toggle(wildcard)}
                        aria-label={`All ${g.label.toLowerCase()} events`}
                      />
                      {g.label}
                    </label>
                    <div className="mt-2 flex flex-col gap-1 pl-6">
                      {g.events.map((ev) => {
                        const name = `crm.${g.entity}.${ev}`;
                        return (
                          <label key={ev} className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              className="accent-brand size-4"
                              checked={groupAll || events.has(name)}
                              disabled={groupAll}
                              onChange={() => toggle(name)}
                              aria-label={name}
                            />
                            {EVENT_LABEL[ev]}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </fieldset>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            <span>
              Filter <span className="text-muted-foreground font-normal">· optional</span>
            </span>
            <Textarea
              aria-label="Webhook filter"
              placeholder={'{ "to": ["<stage id>"] }  — only events whose data matches'}
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="min-h-20 font-mono text-xs"
            />
          </label>
          {error && (
            <p className="text-danger text-sm" role="alert">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!url.trim() || events.size === 0 || save.isPending}
              data-testid="save-webhook"
            >
              {hook ? 'Save' : 'Create webhook'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The one time a signing secret is visible: after create and after rotate. */
function SecretDialog({ hook, onClose }: { hook: WebhookWithSecretView; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(hook.secret);
      setCopied(true);
    } catch (err) {
      // No clipboard (permissions, an insecure origin): the secret is selectable below.
      void err;
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Signing secret</DialogTitle>
          <DialogDescription>
            Give this to the receiving system so it can check each request really came from ReAct.
            It is shown only now; if it is lost, rotate it.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <code
            className="bg-muted min-w-0 flex-1 rounded-lg border px-3 py-2 font-mono text-xs break-all select-all"
            data-testid="webhook-secret"
          >
            {hook.secret}
          </code>
          <Button variant="outline" size="sm" onClick={() => void copy()} aria-label="Copy secret">
            {copied ? <Check /> : <Copy />} {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        {hook.previousSecretExpiresAt && (
          <p className="text-muted-foreground text-xs">
            The old secret keeps working until {when(hook.previousSecretExpiresAt)}, so the receiver
            can switch over without missing events.
          </p>
        )}
        <DialogFooter>
          <Button onClick={onClose}>I have copied it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** How a receiver should handle what it gets: the contract, in one place on the page. */
function ReceiverNotes() {
  return (
    <details className="border-border bg-card/60 rounded-card mt-8 border px-5 py-4 text-sm">
      <summary className="cursor-pointer font-semibold">For the receiving system</summary>
      <ul className="text-muted-foreground mt-3 flex list-disc flex-col gap-1.5 pl-5">
        <li>
          Each request is a JSON POST with the event name, the record, <code>data</code> and{' '}
          <code>changes</code> (old and new values).
        </li>
        <li>
          Verify <code>{WEBHOOK_HEADERS.signature}</code>:{' '}
          <code>t=&lt;seconds&gt;,v1=&lt;hex&gt;</code>, where v1 is HMAC-SHA256 of{' '}
          <code>&lt;t&gt;.&lt;raw body&gt;</code> with the secret. During a rotation a second{' '}
          <code>v1</code> is signed with the old secret. Reject a <code>t</code> more than 5 minutes
          old.
        </li>
        <li>
          Answer 2xx quickly. Anything else is retried with growing gaps for 24 hours, in order per
          record, then marked failed here (replayable).
        </li>
        <li>
          Delivery is at least once: dedupe on the body's <code>id</code> (the event id), which a
          replay keeps.
        </li>
      </ul>
    </details>
  );
}
